import { useState } from 'react';
import { Icon, Text, IconNames, Button, Spinner, Skeleton, Tooltip } from '@sendible/design-system/src';
import { useTranslation } from 'react-i18next';
import { Avatar, Container, ErrorBox, Content, NetworkBadge, StatusArea, ContentTop } from './index.styles';

const URL_REGEX = /(https?:\/\/[^\s]+)/g;

type Props = {
  name: string;
  networkIcon?: IconNames;
  networkUrl?: string;
  errorMessage?: string;
  onRepost: () => void;
  onResolve: () => void;
  id: number;
  isSuccess?: boolean;
  isResolved: boolean;
  isLoading?: boolean;
  useSummaryVersion?: boolean;
};

export const RecipientCard = ({
  name,
  networkIcon,
  networkUrl,
  errorMessage,
  id,
  isLoading,
  isSuccess = false,
  isResolved,
  onRepost,
  onResolve,
  useSummaryVersion,
}: Props) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const initial = (name?.trim()?.[0] ?? '?').toUpperCase();
  const showCheck = isSuccess && isResolved;
  const { t } = useTranslation('calendar');

  const linkedErrorMessage = errorMessage?.split(URL_REGEX).map((part, index) => {
    if (!/^https?:\/\//.test(part)) {
      return part;
    }

    const match = part.match(/^(https?:\/\/[^\s]+?)([).,!?:;]*)$/);
    const href = match?.[1] ?? part;
    const trailingChars = match?.[2] ?? '';

    return (
      <span key={href + index}>
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
        >
          {href}
        </a>
        {trailingChars}
      </span>
    );
  });

  const statusArea = (
    <StatusArea
      data-testid="calendar-status-area"
      className="status-area"
      $isSummaryVersion={!!useSummaryVersion}
    >
      {isLoading ? (
        <Spinner size="xs" />
      ) : (
        <>
          {showCheck && (
            <Tooltip content={t('Sent') as string}>
              <Icon
                name="check"
                color="semanticSuccess"
                customClass="status-icon"
              />
            </Tooltip>
          )}
          {isResolved && !isSuccess && <span className="resolve-button">{t('markedAsRead')}</span>}
          {!isResolved && !isSuccess && (
            <Tooltip content={t('markAsRead') as string}>
              <Button
                customClass="mark-as-read-button"
                onClick={onResolve}
                id={`publish_calendar_mark-as-read-${id}`}
                icon="check_circle"
                size={12}
              />
            </Tooltip>
          )}
          <Tooltip content={t('repost') as string}>
            <Button
              customClass="repost-button"
              onClick={onRepost}
              id={`publish_calendar_repost-${id}`}
              icon="send"
              size={12}
            />
          </Tooltip>
        </>
      )}
    </StatusArea>
  );

  return (
    <Container
      data-testid="calendar-recipient-card-container"
      id={`recipient-card-recipient-${id}`}
    >
      {!useSummaryVersion && (
        <Avatar
          $useBackground={!networkUrl}
          aria-hidden="true"
        >
          {networkUrl ? (
            <>
              {!imageLoaded && (
                <Skeleton
                  height="2.5rem"
                  width="2.5rem"
                />
              )}
              <img
                src={networkUrl}
                alt={name}
                style={{ display: imageLoaded ? 'block' : 'none' }}
                onLoad={() => setImageLoaded(true)}
                onError={(e) => {
                  e.currentTarget.src = '/s3_assets/snd-assets/images/avatar.png';
                  setImageLoaded(true);
                }}
              />
            </>
          ) : (
            <Text
              variation="body_14"
              color="brandLight"
            >
              {initial}
            </Text>
          )}

          {networkIcon && (
            <NetworkBadge data-testid="network-badge">
              <Icon
                name={networkIcon}
                color="brandLight"
                strokeWidth={1.6}
              />
            </NetworkBadge>
          )}
        </Avatar>
      )}

      <Content data-testid="calendar-recipient-content-area">
        {!useSummaryVersion && (
          <ContentTop>
            <Text
              variation="body_12"
              color="brandDarkTint30"
              maxLines={1}
            >
              {name}
            </Text>
            {statusArea}
          </ContentTop>
        )}

        {errorMessage ? (
          <ErrorBox
            data-testid="calendar-recipient-error-box"
            $isSummaryVersion={!!useSummaryVersion}
            $isResolved={isResolved}
          >
            <Text variation="body_12">
              {useSummaryVersion && <strong>{name}: </strong>}
              {linkedErrorMessage}
            </Text>
            {useSummaryVersion && statusArea}
          </ErrorBox>
        ) : null}
      </Content>
    </Container>
  );
};
